import { PageListItem } from "../index";
import { useTranslation } from "../../context/translation";
import dayjs from "dayjs";
import { useDateFormatter } from "../../utils/date";
import { useEffect, useState } from "react";
import { useLinkBuilder } from "../../utils/link";
import { stripTags } from "../../utils/static";


type PartnerChipMenuProps = {
  title?: string;
  allShops?: boolean;
  allShopsPage?: PageListItem;
};

export type DirectSelectEvent = {
  type: string;
  preview?: boolean;
};

const ALLOWED_TAGS = ["b", "i", "u", "strong"];

export default function PartnerChipMenu({ title, allShops = true, allShopsPage }: PartnerChipMenuProps) {
  const { translate } = useTranslation();
  const { getReadableMonth, getDaysUntilNextMonth } = useDateFormatter();
  const { buildLinkByPage } = useLinkBuilder();

  const [selectedType, setSelectedType] = useState<string>("all");

  useEffect(() => {

    const onReverseSelect = (event: CustomEvent<DirectSelectEvent>) => {
      setSelectedType(event.detail.type);
    };

    document.addEventListener("reverseCampaignSelect", onReverseSelect as EventListener);
    return () => {
      document.removeEventListener("reverseCampaignSelect", onReverseSelect as EventListener);
    };
  }, []);

  const directSelect = (type: string, preview = false) => {
    document.dispatchEvent(new CustomEvent<DirectSelectEvent>("campaignSelect", { detail: { type, preview } }));
    setSelectedType(type);
  };

  const now = dayjs().format("MM-YYYY");
  const nextMonth = dayjs().add(1, "month").format("MM-YYYY");
  const showNextMonthChip = Boolean(getDaysUntilNextMonth() < 3);

  return (
    <section className="mamcom_component mam_chip_menu_container">
      <div className="row">
        <div className="col-sm-10 offset-sm-1">
          <header className="container_header">
            <div className="heading"><h2
              dangerouslySetInnerHTML={{ __html: title ? stripTags(title, ...ALLOWED_TAGS) : translate("mam.blocks.partner.chip_menu.title") }} />
            </div>
            {(allShops && allShopsPage) && (
              <a href={buildLinkByPage(allShopsPage)} tabIndex={0}
                 aria-label={translate('mam.static.to_page_aria', translate("mam.blocks.partner.chip_menu.all_shops"))}
                 title={translate("mam.blocks.partner.chip_menu.all_shops")} className="link_to_page">
                <span className="link_text_to_page"
                      data-role="none">{translate("mam.blocks.partner.chip_menu.all_shops")}</span>
                <span className="mam-ico26"></span>
              </a>
            )}
          </header>
          <div className="mam-select-chips">
            <button className={`chip ${selectedType === `recommendations-${now}` ? `chip--active` : ``}`}
                    aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.recommendations"))}
                    onClick={() => directSelect(`recommendations-${now}`)}
                    data-role="none" data-value={`recommendations-${now}`}>
              <div className="text">{translate("mam.blocks.partner.chip_menu.recommendations")}</div>
            </button>
            <button className={`chip ${selectedType === `all` ? `chip--active` : ``}`}
                    onClick={() => directSelect(`all`)}
                    aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.all_shops"))}
                    data-role="none" data-value={`all`}>
              <div className="text">{translate("mam.blocks.partner.chip_menu.all_shops")}</div>
            </button>
            <button className={`chip ${selectedType === `campaign-${now}` ? `chip--active` : ``}`}
                    onClick={() => directSelect(`campaign-${now}`)} data-role="none"
                    aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.month_campaign", getReadableMonth(dayjs().toISOString())))}
                    data-value={`campaign-${now}`}>
              <div
                className="text">{translate("mam.blocks.partner.chip_menu.month_campaign", getReadableMonth(dayjs().toISOString()))}</div>
            </button>
            <button className={`chip ${selectedType === `sale` ? `chip--active` : ``}`}
                    onClick={() => directSelect("sale")} data-role="none"
                    aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.voucher_and_sale"))}
                    data-value="sale">
              <div className="text">{translate("mam.blocks.partner.chip_menu.voucher_and_sale")}</div>
            </button>
            <button className={`chip ${selectedType === `new` ? `chip--active` : ``}`}
                    onClick={() => directSelect("new")}
                    data-role="none"
                    aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.new_shops"))}
                    data-value="new">
              <div className="text">{translate("mam.blocks.partner.chip_menu.new_shops")}</div>
            </button>
            {showNextMonthChip && (
              <button className={`chip ${selectedType === `campaign-${nextMonth}` ? `chip--active` : ``}`}
                      onClick={() => directSelect(`campaign-${nextMonth}`, true)} data-role="none"
                      aria-label={translate("mam.blocks.partner.chip_menu.filter_aria", translate("mam.blocks.partner.chip_menu.next_month_campaign", getReadableMonth(dayjs().add(1, "month").toISOString())))}
                      data-value={`campaign-${nextMonth}`}>
                <div
                  className="text">{translate("mam.blocks.partner.chip_menu.next_month_campaign", getReadableMonth(dayjs().add(1, "month").toISOString()))}</div>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
